import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    GroupTrainingAccess,
    GroupTrainingAccessRanksResponse,
    GroupTrainingAccessResponse,
    GroupTrainingAccessUpsertRequest,
    GroupTrainingAccessVesselGroupsResponse,
    GroupTrainingRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/groupTrainingAccess.api.ts';
import {
    createApiGroupTrainingAccess,
    deleteApiGroupTrainingAccess,
    getApiGroupTrainingAccess,
    getApiGroupTrainingAccessRanks,
    getApiGroupTrainingAccessVesselGroups,
    updateApiGroupTrainingAccess,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/groupTrainingAccess.api.ts';
import { extractError } from '@utils/slice.ts';

interface GroupTrainingAccessError {
    message?: string;
    [key: string]: any;
}

interface GroupTrainingAccessState {
    error: GroupTrainingAccessError | null;

    groupTrainingAccessData: GroupTrainingAccessResponse | null;
    groupTrainingAccessVesselGroupsData: GroupTrainingAccessVesselGroupsResponse | null;
    groupTrainingAccessRanksData: GroupTrainingAccessRanksResponse | null;

    isGetGroupTrainingAccessPending: boolean;
    isCreateGroupTrainingAccessPending: boolean;
    isUpdateGroupTrainingAccessPending: boolean;
    isDeleteGroupTrainingAccessPending: boolean;
    isGetGroupTrainingAccessVesselGroupsPending: boolean;
    isGetGroupTrainingAccessRanksPending: boolean;
}

const initialState: GroupTrainingAccessState = {
    error: null,

    groupTrainingAccessData: null,
    groupTrainingAccessVesselGroupsData: null,
    groupTrainingAccessRanksData: null,

    isGetGroupTrainingAccessPending: false,
    isCreateGroupTrainingAccessPending: false,
    isUpdateGroupTrainingAccessPending: false,
    isDeleteGroupTrainingAccessPending: false,
    isGetGroupTrainingAccessVesselGroupsPending: false,
    isGetGroupTrainingAccessRanksPending: false,
};

export const getGroupTrainingAccess = createAsyncThunk<
    GroupTrainingAccessResponse,
    { tenantAlias: string; payload: GroupTrainingRequest },
    { rejectValue: GroupTrainingAccessError }
>('groupTrainingAccess/getGroupTrainingAccess', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiGroupTrainingAccess(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createGroupTrainingAccess = createAsyncThunk<
    GroupTrainingAccess,
    { tenantAlias: string; payload: GroupTrainingAccessUpsertRequest },
    { rejectValue: GroupTrainingAccessError }
>('groupTrainingAccess/createGroupTrainingAccess', ({ tenantAlias, payload }, { rejectWithValue }) =>
    createApiGroupTrainingAccess(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateGroupTrainingAccess = createAsyncThunk<
    GroupTrainingAccess,
    { tenantAlias: string; accessId: number; payload: GroupTrainingAccessUpsertRequest },
    { rejectValue: GroupTrainingAccessError }
>('groupTrainingAccess/updateGroupTrainingAccess', ({ tenantAlias, accessId, payload }, { rejectWithValue }) =>
    updateApiGroupTrainingAccess(tenantAlias, accessId, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteGroupTrainingAccess = createAsyncThunk<
    any,
    { tenantAlias: string; accessId: number },
    { rejectValue: GroupTrainingAccessError }
>('groupTrainingAccess/deleteGroupTrainingAccess', ({ tenantAlias, accessId }, { rejectWithValue }) =>
    deleteApiGroupTrainingAccess(tenantAlias, accessId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getGroupTrainingAccessVesselGroups = createAsyncThunk<
    GroupTrainingAccessVesselGroupsResponse,
    { tenantAlias: string; payload: GroupTrainingRequest },
    { rejectValue: GroupTrainingAccessError }
>('groupTrainingAccess/getGroupTrainingAccessVesselGroups', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiGroupTrainingAccessVesselGroups(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getGroupTrainingAccessRanks = createAsyncThunk<
    GroupTrainingAccessRanksResponse,
    { tenantAlias: string; payload: GroupTrainingRequest },
    { rejectValue: GroupTrainingAccessError }
>('groupTrainingAccess/getGroupTrainingAccessRanks', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiGroupTrainingAccessRanks(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const groupTrainingAccessSlice = createSlice({
    name: 'groupTrainingAccess',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getGroupTrainingAccess.pending, (state) => {
            state.isGetGroupTrainingAccessPending = true;
            state.error = null;
        });
        builder.addCase(getGroupTrainingAccess.fulfilled, (state, action) => {
            state.isGetGroupTrainingAccessPending = false;
            state.groupTrainingAccessData = action.payload;
        });
        builder.addCase(getGroupTrainingAccess.rejected, (state, action) => {
            state.isGetGroupTrainingAccessPending = false;
            state.error = extractError(action);
        });

        builder.addCase(createGroupTrainingAccess.pending, (state) => {
            state.isCreateGroupTrainingAccessPending = true;
            state.error = null;
        });
        builder.addCase(createGroupTrainingAccess.fulfilled, (state) => {
            state.isCreateGroupTrainingAccessPending = false;
        });
        builder.addCase(createGroupTrainingAccess.rejected, (state, action) => {
            state.isCreateGroupTrainingAccessPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateGroupTrainingAccess.pending, (state) => {
            state.isUpdateGroupTrainingAccessPending = true;
            state.error = null;
        });
        builder.addCase(updateGroupTrainingAccess.fulfilled, (state) => {
            state.isUpdateGroupTrainingAccessPending = false;
        });
        builder.addCase(updateGroupTrainingAccess.rejected, (state, action) => {
            state.isUpdateGroupTrainingAccessPending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteGroupTrainingAccess.pending, (state) => {
            state.isDeleteGroupTrainingAccessPending = true;
            state.error = null;
        });
        builder.addCase(deleteGroupTrainingAccess.fulfilled, (state) => {
            state.isDeleteGroupTrainingAccessPending = false;
        });
        builder.addCase(deleteGroupTrainingAccess.rejected, (state, action) => {
            state.isDeleteGroupTrainingAccessPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getGroupTrainingAccessVesselGroups.pending, (state) => {
            state.isGetGroupTrainingAccessVesselGroupsPending = true;
            state.error = null;
        });
        builder.addCase(getGroupTrainingAccessVesselGroups.fulfilled, (state, action) => {
            state.isGetGroupTrainingAccessVesselGroupsPending = false;
            state.groupTrainingAccessVesselGroupsData = action.payload;
        });
        builder.addCase(getGroupTrainingAccessVesselGroups.rejected, (state, action) => {
            state.isGetGroupTrainingAccessVesselGroupsPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getGroupTrainingAccessRanks.pending, (state) => {
            state.isGetGroupTrainingAccessRanksPending = true;
            state.error = null;
        });
        builder.addCase(getGroupTrainingAccessRanks.fulfilled, (state, action) => {
            state.isGetGroupTrainingAccessRanksPending = false;
            state.groupTrainingAccessRanksData = action.payload;
        });
        builder.addCase(getGroupTrainingAccessRanks.rejected, (state, action) => {
            state.isGetGroupTrainingAccessRanksPending = false;
            state.error = extractError(action);
        });
    },
});

export default groupTrainingAccessSlice.reducer;
